import React, { useState } from 'react';
import { Check, IndianRupee, Calendar, Headphones, ChevronDown, Bike, Navigation, Car } from 'lucide-react';
import { DRIVER_BENEFITS, DRIVE_CITIES, VEHICLES } from '../mock';

const VEHICLE_ICONS = { 'Bike': Bike, 'Auto': Navigation, 'Mini / Sedan': Car, 'SUV': Car };

export default function DriveWithUs() {
  const [form, setForm] = useState({ name: '', phone: '', city: DRIVE_CITIES[0], vehicle: 'Bike' });
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.length < 10) return;
    setSubmitted(true);
    try {
      const existing = JSON.parse(localStorage.getItem('otw_drive_apps') || '[]');
      existing.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('otw_drive_apps', JSON.stringify(existing));
    } catch (_e) {
      /* ignore storage errors */
    }
  };

  return (
    <section id="drive" className="bg-[#FFF8F2] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#FF6A1A]/10 blur-3xl pointer-events-none" />
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* Left */}
        <div>
          <span className="text-[11px] font-semibold tracking-[0.18em] text-[#FF6A1A]">EARN WITH ON THE WAY</span>
          <h2 className="mt-3 text-[34px] md:text-[48px] font-semibold tracking-[-0.02em] text-[#0f0f10] leading-[1.05]">
            Drive when you want.
            <br /> Get paid daily.
          </h2>
          <p className="mt-5 text-[15px] md:text-[17px] text-[#3a3a3d] leading-relaxed max-w-lg">
            Keep up to <span className="font-semibold text-[#0f0f10]">88%</span> of every fare. Daily UPI settlements. Free training and 24/7 support in Bangalore, Mumbai, Delhi NCR, and 9 more cities.
          </p>

          <ul className="mt-7 space-y-3">
            {DRIVER_BENEFITS.map((b) => (
              <li key={b} className="flex items-center gap-3 text-[15px] text-[#0f0f10]">
                <span className="w-6 h-6 rounded-full bg-[#FF6A1A] text-white grid place-items-center flex-shrink-0">
                  <Check size={14} strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            {[
              { icon: IndianRupee, label: 'Daily payouts' },
              { icon: Calendar,    label: 'Flexible hours' },
              { icon: Headphones,  label: '24/7 support' },
            ].map((x) => {
              const I = x.icon;
              return (
                <div key={x.label} className="bg-white/70 backdrop-blur rounded-2xl p-4 ring-1 ring-black/[0.04]">
                  <I size={20} className="text-[#FF6A1A]" />
                  <div className="mt-2 text-[12.5px] font-semibold text-[#0f0f10] leading-tight">{x.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Form */}
        <div className="bg-white rounded-3xl p-7 md:p-9 shadow-[0_30px_80px_-30px_rgba(15,15,16,0.18)] ring-1 ring-black/[0.04]">
          <h3 className="text-[22px] md:text-[24px] font-semibold text-[#0f0f10] tracking-[-0.01em]">Get started in 60 seconds</h3>
          <p className="mt-1.5 text-[13.5px] text-[#6b6b70]">We’ll call you to verify your documents.</p>

          {submitted ? (
            <div className="mt-7 rounded-2xl bg-[#EAF8EE] p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-[#0E8A3F] text-white grid place-items-center mx-auto">
                <Check size={22} strokeWidth={3} />
              </div>
              <div className="mt-3 text-[16px] font-semibold text-[#0f0f10]">Application received</div>
              <div className="mt-1 text-[13.5px] text-[#3a3a3d]">Our {form.city} team will call {form.phone} within 24 hours.</div>
              <button onClick={() => { setSubmitted(false); setForm({ name: '', phone: '', city: DRIVE_CITIES[0], vehicle: 'Bike' }); }} className="mt-5 text-[13px] font-semibold text-[#FF6A1A] hover:underline">Submit another</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <Field label="FULL NAME">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Rahul Sharma"
                  className="w-full bg-[#f7f7f5] rounded-xl px-4 py-3.5 text-[15px] text-[#0f0f10] placeholder:text-[#a3a3a6] outline-none focus:ring-2 focus:ring-[#FF6A1A]/40 transition"
                />
              </Field>

              <Field label="PHONE NUMBER">
                <div className="flex items-stretch gap-2">
                  <span className="inline-flex items-center px-3 bg-[#f7f7f5] rounded-xl text-[14px] font-semibold text-[#0f0f10]">+91</span>
                  <input
                    type="tel"
                    required
                    inputMode="numeric"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
                    placeholder="9876543210"
                    className="flex-1 bg-[#f7f7f5] rounded-xl px-4 py-3.5 text-[15px] text-[#0f0f10] placeholder:text-[#a3a3a6] outline-none focus:ring-2 focus:ring-[#FF6A1A]/40 transition"
                  />
                </div>
              </Field>

              <Field label="CITY">
                <div className="relative">
                  <select
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="appearance-none w-full bg-[#f7f7f5] rounded-xl px-4 py-3.5 pr-10 text-[15px] text-[#0f0f10] outline-none focus:ring-2 focus:ring-[#FF6A1A]/40 transition"
                  >
                    {DRIVE_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#6b6b70] pointer-events-none" />
                </div>
              </Field>

              <Field label="YOUR VEHICLE">
                <div className="grid grid-cols-4 gap-2">
                  {VEHICLES.map((v) => {
                    const I = VEHICLE_ICONS[v] || Car;
                    const active = form.vehicle === v;
                    return (
                      <button
                        type="button"
                        key={v}
                        onClick={() => setForm({ ...form, vehicle: v })}
                        className={`flex flex-col items-center justify-center gap-1.5 rounded-xl py-3 text-[11.5px] font-semibold transition-colors ${
                          active ? 'bg-[#0f0f10] text-white' : 'bg-[#f7f7f5] text-[#0f0f10] hover:bg-[#eeefe9]'
                        }`}
                      >
                        <I size={18} />
                        <span>{v}</span>
                      </button>
                    );
                  })}
                </div>
              </Field>

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6A1A] hover:bg-[#ff5a05] text-white font-semibold rounded-full px-6 py-3.5 text-[15px] transition-colors shadow-[0_18px_40px_-15px_rgba(255,106,26,0.6)]"
              >
                Apply to drive
              </button>
              <p className="text-[11.5px] text-[#a3a3a6] text-center">By submitting, you agree to be contacted by our team.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] font-semibold tracking-[0.18em] text-[#6b6b70]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
